import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Menu, X, ChevronDown, Sparkles } from 'lucide-react';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // 스크롤 감지
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 모바일 메뉴 열릴 때 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // 현재 경로 체크
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // 네비게이션 아이템
  const navItems = [
    {
      label: '서비스',
      hasDropdown: true,
      dropdownId: 'service',
      items: [
        {
          to: '/service/ab-test',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          ),
          title: 'A/B 테스트',
          desc: '데이터 기반의 실험과 검증',
          hoverBg: 'hover:bg-blue-50',
          iconBg: 'bg-blue-50 text-blue-600',
          iconHover: 'group-hover/item:bg-blue-100',
          textHover: 'group-hover/item:text-blue-600'
        },
        {
          to: '/service/heatmap',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
          ),
          title: '히트맵 분석',
          desc: '사용자 행동을 시각적으로 분석',
          hoverBg: 'hover:bg-orange-50',
          iconBg: 'bg-orange-50 text-orange-600',
          iconHover: 'group-hover/item:bg-orange-100',
          textHover: 'group-hover/item:text-orange-600'
        },
        {
          to: '/service/funnel',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          ),
          title: '퍼널 분석',
          desc: '단계별 전환율과 이탈 구간 분석',
          hoverBg: 'hover:bg-purple-50',
          iconBg: 'bg-purple-50 text-purple-600',
          iconHover: 'group-hover/item:bg-purple-100',
          textHover: 'group-hover/item:text-purple-600'
        },
        {
          to: '/service/replay',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          ),
          title: '세션 녹화',
          desc: '방문자 화면을 그대로 재생',
          badge: 'NEW',
          hoverBg: 'hover:bg-cyan-50',
          iconBg: 'bg-cyan-50 text-cyan-600',
          iconHover: 'group-hover/item:bg-cyan-100',
          textHover: 'group-hover/item:text-cyan-600'
        },
        {
          to: '/service/personalization',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ),
          title: '개인화',
          desc: '고객마다 다른 내용을 노출',
          badge: 'NEW',
          badgeBg: 'bg-pink-600',
          hoverBg: 'hover:bg-pink-50',
          iconBg: 'bg-pink-50 text-pink-600',
          iconHover: 'group-hover/item:bg-pink-100',
          textHover: 'group-hover/item:text-pink-600'
        },
        {
          to: '/service/popup',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
          ),
          title: '팝업/배너',
          desc: '이탈 방지 · 전환 유도 캠페인',
          hoverBg: 'hover:bg-emerald-50',
          iconBg: 'bg-emerald-50 text-emerald-600',
          iconHover: 'group-hover/item:bg-emerald-100',
          textHover: 'group-hover/item:text-emerald-600'
        }
      ]
    },
    { label: '가이드', to: 'https://docs.switchuplab.com/', external: true },
    { label: '요금제', to: '/pricing' },
    { label: '도입 문의', to: '/contact' },
    {
      label: '무료 도구',
      hasDropdown: true,
      dropdownId: 'tools',
      items: [
        {
          to: '/tools/effect',
          emoji: '💰',
          title: '전환율 개선 효과 계산기',
          desc: '매출 증가 효과 분석',
          hoverBg: 'hover:bg-yellow-50',
          iconBg: 'bg-yellow-50 text-yellow-600',
          iconHover: 'group-hover/item:bg-yellow-100',
          textHover: 'group-hover/item:text-yellow-600'
        },
        {
          to: '/tools/roi',
          emoji: '💵',
          title: 'ROI(순수익) 계산기',
          desc: 'ROAS와 ROI 동시 계산',
          hoverBg: 'hover:bg-red-50',
          iconBg: 'bg-red-50 text-red-600',
          iconHover: 'group-hover/item:bg-red-100',
          textHover: 'group-hover/item:text-red-600'
        },
        {
          to: '/tools/duration',
          emoji: '⏱️',
          title: 'A/B 테스트 기간 계산기',
          desc: '최적 테스트 기간 산출',
          hoverBg: 'hover:bg-purple-50',
          iconBg: 'bg-purple-50 text-purple-600',
          iconHover: 'group-hover/item:bg-purple-100',
          textHover: 'group-hover/item:text-purple-600'
        },
        {
          to: '/tools/significance',
          emoji: '✅',
          title: 'A/B 테스트 결과 판별기',
          desc: '테스트 결과의 신뢰도 확인',
          hoverBg: 'hover:bg-green-50',
          iconBg: 'bg-green-50 text-green-600',
          iconHover: 'group-hover/item:bg-green-100',
          textHover: 'group-hover/item:text-green-600'
        }
      ],
      footerLink: {
        to: '/tools',
        title: '전체 도구 보기',
        desc: '모든 계산기 한눈에 보기'
      }
    }
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
        ? 'bg-white/95 backdrop-blur-xl shadow-lg shadow-gray-900/5 border-b border-gray-200/50'
        : 'bg-white/80 backdrop-blur-lg border-b border-gray-100'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">

            {/* 로고 */}
            <div
              onClick={() => navigate('/')}
              className="cursor-pointer group"
            >
              <span className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                SWITCHUP
              </span>
            </div>

            {/* 데스크톱 네비게이션 */}
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((item, index) => (
                item.hasDropdown ? (
                  // 드롭다운 메뉴
                  <div
                    key={index}
                    className="relative group"
                    onMouseEnter={() => setActiveDropdown(item.dropdownId)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <button className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${isActive(`/${item.dropdownId}`) || activeDropdown === item.dropdownId
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                      }`}>
                      <span>{item.label}</span>
                      {/* 드롭다운 indicator dot */}
                      <span className="relative flex items-center">
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === item.dropdownId ? 'rotate-180' : ''}`} />
                      </span>
                    </button>

                    {/* 드롭다운 패널 */}
                    <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ${activeDropdown === item.dropdownId
                      ? 'opacity-100 visible translate-y-0'
                      : 'opacity-0 invisible translate-y-2'
                      }`}>
                      <div className="bg-white rounded-2xl shadow-xl shadow-gray-900/10 border border-gray-100 py-2 min-w-[280px] overflow-hidden">
                        <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          {item.label}
                        </div>

                        {item.items.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.to}
                            className={`flex items-center gap-3 px-4 py-3 transition-colors group/item ${subItem.hoverBg}`}
                          >
                            <span className={`w-10 h-10 rounded-xl ${subItem.iconBg} flex items-center justify-center transition-all ${subItem.iconHover} group-hover/item:scale-110`}>
                              {subItem.icon || <span className="text-lg">{subItem.emoji}</span>}
                            </span>
                            <div>
                              <div className={`font-medium text-gray-700 ${subItem.textHover} transition-colors flex items-center gap-2`}>
                                {subItem.title}
                                {subItem.badge && (
                                  <span className={`px-1.5 py-0.5 rounded-full ${subItem.badgeBg || 'bg-cyan-600'} text-white text-[10px] font-bold leading-none`}>
                                    {subItem.badge}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-400 mt-0.5">{subItem.desc}</div>
                            </div>
                          </Link>
                        ))}

                        {item.footerLink && (
                          <>
                            <div className="border-t border-gray-100 my-2" />
                            <Link
                              to={item.footerLink.to}
                              className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-blue-50 group/item"
                            >
                              <span className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover/item:bg-blue-100">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                </svg>
                              </span>
                              <div>
                                <div className="font-medium text-blue-600">{item.footerLink.title}</div>
                                <div className="text-xs text-gray-400 mt-0.5">{item.footerLink.desc}</div>
                              </div>
                            </Link>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  // 일반 링크
                  <Link
                    key={index}
                    to={item.to}
                    target={item.external ? '_blank' : undefined}
                    rel={item.external ? 'noopener noreferrer' : undefined}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${isActive(item.to)
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                  >
                    {item.label}
                  </Link>
                )
              ))}
            </div>

            {/* 우측 CTA 버튼 */}
            <div className="hidden lg:flex items-center gap-3">
              {isAuthenticated ? (
                <button
                  onClick={logout}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  로그아웃
                </button>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  로그인
                </button>
              )}

              {/* Primary CTA - Gradient + Glow */}
              <button
                onClick={() => window.location.href = `${process.env.REACT_APP_APP_DOMAIN}/workspaces`}
                className="relative group px-5 py-2.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5 transition-all duration-300"
              >
                {/* Glow effect */}
                <span className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />
                <span className="relative flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4" />
                  시작하기
                </span>
              </button>
            </div>

            {/* 모바일 메뉴 버튼 */}
            <button
              onClick={() => setIsOpen(true)}
              className="lg:hidden p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* 모바일 메뉴 - 풀스크린 오버레이 */}
      <div className={`fixed inset-0 z-[100] lg:hidden transition-all duration-300 ${isOpen ? 'visible' : 'invisible'
        }`}>
        {/* 배경 오버레이 */}
        <div
          className={`absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'
            }`}
          onClick={() => setIsOpen(false)}
        />

        {/* 메뉴 패널 */}
        <div className={`absolute inset-y-0 right-0 w-full max-w-sm bg-white shadow-2xl transition-transform duration-300 ease-out flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}>
          {/* 헤더 */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-shrink-0">
            <span className="text-lg font-bold text-gray-900">SWITCHUP</span>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* 메뉴 컨텐츠 */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6 min-h-0">
            {/* 서비스 섹션 */}
            <div>
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">서비스</div>
              <div className="space-y-2">
                <Link
                  to="/service/ab-test"
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isActive('/service/ab-test') ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50'
                    }`}
                >
                  <span className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </span>
                  <div>
                    <div className="font-medium">A/B 테스트</div>
                    <div className="text-xs text-gray-400">데이터 기반의 실험과 검증</div>
                  </div>
                </Link>

                <Link
                  to="/service/heatmap"
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isActive('/service/heatmap') ? 'bg-orange-50 text-orange-600' : 'hover:bg-gray-50'
                    }`}
                >
                  <span className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                    </svg>
                  </span>
                  <div>
                    <div className="font-medium">히트맵 분석</div>
                    <div className="text-xs text-gray-400">사용자 행동을 시각적으로 분석</div>
                  </div>
                </Link>

                <Link
                  to="/service/funnel"
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isActive('/service/funnel') ? 'bg-purple-50 text-purple-600' : 'hover:bg-gray-50'
                    }`}
                >
                  <span className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                  </span>
                  <div>
                    <div className="font-medium">퍼널 분석</div>
                    <div className="text-xs text-gray-400">단계별 전환율과 이탈 구간 분석</div>
                  </div>
                </Link>

                <Link
                  to="/service/replay"
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isActive('/service/replay') ? 'bg-cyan-50 text-cyan-600' : 'hover:bg-gray-50'
                    }`}
                >
                  <span className="w-10 h-10 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </span>
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      세션 녹화
                      <span className="px-1.5 py-0.5 rounded-full bg-cyan-600 text-white text-[10px] font-bold leading-none">NEW</span>
                    </div>
                    <div className="text-xs text-gray-400">방문자 화면을 그대로 재생</div>
                  </div>
                </Link>

                <Link
                  to="/service/personalization"
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isActive('/service/personalization') ? 'bg-pink-50 text-pink-600' : 'hover:bg-gray-50'
                    }`}
                >
                  <span className="w-10 h-10 rounded-xl bg-pink-50 text-pink-600 flex items-center justify-center">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </span>
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      개인화
                      <span className="px-1.5 py-0.5 rounded-full bg-pink-600 text-white text-[10px] font-bold leading-none">NEW</span>
                    </div>
                    <div className="text-xs text-gray-400">고객마다 다른 내용을 노출</div>
                  </div>
                </Link>

                <Link
                  to="/service/popup"
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isActive('/service/popup') ? 'bg-emerald-50 text-emerald-600' : 'hover:bg-gray-50'
                    }`}
                >
                  <span className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                    </svg>
                  </span>
                  <div>
                    <div className="font-medium">팝업/배너</div>
                    <div className="text-xs text-gray-400">이탈 방지 · 전환 유도 캠페인</div>
                  </div>
                </Link>
              </div>
            </div>

            {/* 일반 링크 */}
            <div>
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">메뉴</div>
              <div className="space-y-1">
                <Link
                  to="https://docs.switchuplab.com/"
                  target="_blank"
                  onClick={() => setIsOpen(false)}
                  className="block px-3 py-2.5 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-gray-50 font-medium transition-colors"
                >
                  가이드
                </Link>
                <Link
                  to="/pricing"
                  onClick={() => setIsOpen(false)}
                  className={`block px-3 py-2.5 rounded-lg font-medium transition-colors ${isActive('/pricing') ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                >
                  요금제
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className={`block px-3 py-2.5 rounded-lg font-medium transition-colors ${isActive('/contact') ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                >
                  도입 문의
                </Link>
              </div>
            </div>

            {/* 무료 도구 */}
            <div>
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">무료 도구</div>
              <div className="space-y-2">
                {[
                  { to: '/tools/effect', emoji: '💰', title: '전환율 개선 효과', desc: '매출 증가 효과 분석' },
                  { to: '/tools/roi', emoji: '💵', title: 'ROI 계산기', desc: 'ROAS와 ROI 계산' },
                  { to: '/tools/duration', emoji: '⏱️', title: '테스트 기간 계산기', desc: '최적 기간 산출' },
                  { to: '/tools/significance', emoji: '✅', title: '결과 판별기', desc: '신뢰도 확인' },
                ].map((tool, index) => (
                  <Link
                    key={index}
                    to={tool.to}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isActive(tool.to) ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                  >
                    <span className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-lg">
                      {tool.emoji}
                    </span>
                    <div>
                      <div className="font-medium text-gray-700">{tool.title}</div>
                      <div className="text-xs text-gray-400">{tool.desc}</div>
                    </div>
                  </Link>
                ))}
              </div>

              <Link
                to="/tools"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 mt-3 px-3 py-2 text-sm text-blue-600 font-medium hover:underline"
              >
                전체 도구 보기 →
              </Link>
            </div>
          </div>

          {/* 하단 CTA */}
          <div className="p-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
            <div className="grid grid-cols-2 gap-3">
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    logout();
                    setIsOpen(false);
                  }}
                  className="px-4 py-3 rounded-xl text-gray-600 font-medium border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
                >
                  로그아웃
                </button>
              ) : (
                <button
                  onClick={() => {
                    navigate('/login');
                    setIsOpen(false);
                  }}
                  className="px-4 py-3 rounded-xl text-gray-600 font-medium border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
                >
                  로그인
                </button>
              )}
              <button
                onClick={() => {
                  window.location.href = `${process.env.REACT_APP_APP_DOMAIN}/workspaces`;
                  setIsOpen(false);
                }}
                className="px-4 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold shadow-lg shadow-blue-500/25 hover:shadow-xl transition-all"
              >
                시작하기
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;